export const ITEM_TYPES = {
  INVOICE: 'invoice',
  PRODUCT: 'product',
  SERVICE: 'service',
  PACKAGE: 'package'
};

export const ITEM_TABS = [
  { value: ITEM_TYPES.INVOICE, label: 'Invoices' },
  { value: ITEM_TYPES.SERVICE, label: 'Services' },
  { value: ITEM_TYPES.PRODUCT, label: 'Products' },
  { value: ITEM_TYPES.PACKAGE, label: 'Packages' }
];

export const ITEM_TYPE_LABELS = {
  [ITEM_TYPES.INVOICE]: 'Invoice',
  [ITEM_TYPES.PRODUCT]: 'Product',
  [ITEM_TYPES.SERVICE]: 'Service',
  [ITEM_TYPES.PACKAGE]: 'Package'
};

// `type` on a saved invoice line is not the picker tab: procedures carry their charge type
// ('self_pay' | 'primary' | 'unapplied') and products/packages a capitalised kind. Items sold from
// the payment screen are charged at their self-pay price.
export const INVOICE_LINE_TYPES = {
  [ITEM_TYPES.SERVICE]: 'self_pay',
  [ITEM_TYPES.PRODUCT]: 'Product',
  [ITEM_TYPES.PACKAGE]: 'Package'
};

export const ITEM_TYPE_ICONS = {
  [ITEM_TYPES.INVOICE]: 'file-invoice-filled',
  [ITEM_TYPES.PRODUCT]: 'box-filled',
  [ITEM_TYPES.SERVICE]: 'treatment-service-filled',
  [ITEM_TYPES.PACKAGE]: 'packages-mono'
};

export const itemUnitPrice = (item) =>
  Number(item?.self_pay ?? item?.price ?? item?.amount_cents ?? item?.standard_charge ?? 0);

export const itemQuantity = (item) => Number(item?.quantity ?? item?.qty ?? 1);

export const itemTotal = (item) =>
  item?.type === ITEM_TYPES.INVOICE
    ? Number(item?.balance ?? item?.total_amount_cents ?? 0)
    : itemUnitPrice(item) * itemQuantity(item);

export const itemDiscount = (item) => Number(item?.discount?.amount_cents || 0);

export const packageServices = (item) => (Array.isArray(item?.procedures) ? item.procedures : []);

const packageProducts = (item) => (Array.isArray(item?.products) ? item.products : []);

export const packageContents = (item) => [
  ...packageServices(item).map((line, index) => ({
    line,
    collection: 'procedures',
    kind: 'Procedure',
    index
  })),
  ...packageProducts(item).map((line, index) => ({
    line,
    collection: 'products',
    kind: 'Product',
    index
  }))
];

// How many of a package the stock allows: the scarcest product's live stock (`stock`, from the
// packages API) over the units one package uses. A package of services only is not limited.
const unitsPerPackage = (product) => Math.max(1, Number(product?.quantity) || 1);
const packagesCovered = (product) =>
  Math.floor((Number(product?.stock) || 0) / unitsPerPackage(product));

export const packageAvailableQuantity = (item) => {
  const products = packageProducts(item);
  if (!products.length) return Infinity;

  return Math.min(...products.map(packagesCovered));
};

// The products that can't cover a single package, with what one package needs and what's on hand.
export const packageShortProducts = (item) =>
  packageProducts(item)
    .filter((product) => packagesCovered(product) < 1)
    .map((product) => ({
      name: product?.name,
      needed: unitsPerPackage(product),
      inStock: Math.max(0, Number(product?.stock) || 0)
    }));

// Mirrors the backend's calcTax: rates are basis points of the line amount after its discount.
const lineTax = (amount, line) => {
  const rate = Number(line?.local_tax_rate || 0) + Number(line?.state_tax_rate || 0);
  if (!rate) return 0;

  return Math.round((Math.max(0, amount - itemDiscount(line)) * rate) / 10000);
};

// A package service's tax, on its own price (the backend does not scale it by package quantity).
export const serviceLineTax = (service) =>
  lineTax(Number(service?.total_amount_cents ?? service?.total_cost_cents ?? 0), service);

export const itemTax = (item) => {
  if (item?.type === ITEM_TYPES.PACKAGE) {
    return packageContents(item).reduce((sum, { line }) => sum + serviceLineTax(line), 0);
  }

  return lineTax(itemTotal(item), item);
};

const packageDiscount = (item) =>
  packageContents(item).reduce((sum, { line }) => sum + itemDiscount(line), 0);

export const itemNetTotal = (item) => {
  if (item?.type === ITEM_TYPES.PRODUCT) {
    return Math.max(0, itemTotal(item) - itemDiscount(item)) + itemTax(item);
  }
  if (item?.type === ITEM_TYPES.PACKAGE) {
    return Math.max(0, itemTotal(item) - packageDiscount(item)) + itemTax(item);
  }

  return itemTotal(item);
};

export const itemsTotal = (items = []) => items.reduce((sum, item) => sum + itemNetTotal(item), 0);

// "Products · Services · Packages" — only the types actually present, in tab order.
export const itemTypesSummary = (items = []) => {
  const present = new Set(items.map((item) => item.type));

  return ITEM_TABS.filter((tab) => present.has(tab.value))
    .map((tab) => tab.label)
    .join(' · ');
};
